import { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { DeviceState } from '@/types';
import { activate, registerWithServer } from '@/core/device-identity';
import { logger } from '@/core/logger';
import { pollPairingStatus } from '@/core/api-client';
import { formatCode, getOrCreatePairingCode } from '@/core/pairing';
import { usePixelDrift } from '@/playback/AntiBurnIn';

interface ProvisionScreenProps {
  onProvisioned: (state: DeviceState) => void;
}

const POLL_INTERVAL_MS = 5_000;

// Design tokens — aligned with SensarIdle so all "device chrome" screens
// feel like one product.
const COL = {
  bg: '#0A0A0A',
  bone: '#F4F1EC',
  orange: '#FF6B2B',
  graySoft: 'rgba(244,241,236,0.55)',
  grayFaint: 'rgba(244,241,236,0.32)',
  rule: 'rgba(244,241,236,0.08)',
  panel: 'rgba(244,241,236,0.04)',
};

const FONT_DISPLAY = "'Space Grotesk', sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

/**
 * Initial pairing screen.
 *
 * Zero-interaction on the TV: once an admin clicks Connect inside the
 * dashboard, the player polls /boards/pair/:code/status, sees ready
 * flip true, and runs activate() + confirm() on its own. Mounting a
 * fleet at scale means no one ever needs to reach for a remote on the
 * physical display — exactly what signage deployments demand.
 *
 * Visual brief: matches the rest of the device chrome (SensarIdle,
 * playback overlays) — black canvas, Space Grotesk display type,
 * orange accent reserved for the Sensar mark + active states.
 */
export default function ProvisionScreen({ onProvisioned }: ProvisionScreenProps) {
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [phase, setPhase] = useState<
    'init' | 'waiting' | 'pairing' | 'error'
  >('init');
  const [error, setError] = useState<string | null>(null);
  // Decorative pulse on the QR — increments every poll tick.
  const [tick, setTick] = useState(0);
  const activatingRef = useRef(false);

  // ── Bootstrap pairing code + server registration ───────────────────
  useEffect(() => {
    void (async () => {
      const code = await getOrCreatePairingCode();
      setPairingCode(code);
      try {
        await registerWithServer(code);
        logger.info('[Provision] Pairing code registered', {
          code: formatCode(code),
        });
        setPhase('waiting');
      } catch (err) {
        logger.warn('[Provision] Could not register on server (offline?)', {
          err: String(err),
        });
        // Still move to "waiting" — server may come back, polling will catch up.
        setPhase('waiting');
      }
    })();
  }, []);

  // ── Auto-pair loop ────────────────────────────────────────────────
  useEffect(() => {
    if (!pairingCode || phase !== 'waiting') return;

    const tryPair = async () => {
      if (activatingRef.current) return;
      try {
        const status = await pollPairingStatus(pairingCode);
        setTick((t) => t + 1);
        if (!status.ready) return;

        // Admin clicked Connect in the dashboard → run the
        // challenge-response handshake automatically.
        activatingRef.current = true;
        setPhase('pairing');
        logger.info('[Provision] Code paired by admin — activating', {
          code: formatCode(pairingCode),
        });
        const state = await activate(pairingCode);
        logger.info('[Provision] Activation complete', { boardId: state.boardId });
        onProvisioned(state);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Connection failed';
        if (msg.includes('404') || msg.includes('not found')) {
          // Code not yet registered on server (transient on a slow
          // initial registerWithServer). Let the loop retry.
          return;
        }
        logger.error('[Provision] Auto-pair error', { err: msg });
        setError(humanizeError(msg));
        setPhase('error');
        activatingRef.current = false;
      }
    };

    void tryPair();
    const id = setInterval(() => void tryPair(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [pairingCode, phase, onProvisioned]);

  const formattedCode = pairingCode ? formatCode(pairingCode) : '— — — — — — — —';
  const drift = usePixelDrift();

  return (
    <div style={{ ...styles.container, ...drift }}>
      {/* Subtle grid + radial vignette */}
      <BackgroundChrome tick={tick} />

      {/* Top bar */}
      <div style={styles.topBar}>
        <Logo height={26} />
        <span style={styles.status}>
          <Dot color={phase === 'pairing' ? COL.orange : COL.bone} pulse />
          {phaseLabel(phase)}
        </span>
      </div>

      {/* Center stack */}
      <div style={styles.center}>
        <div style={styles.eyebrow}>NEW DEVICE</div>

        <h1 style={styles.title}>Pair this display</h1>

        <p style={styles.subtitle}>
          Open the Sensar Dashboard, paste the code below, and click <em>Connect</em>.
          <br />
          The display will pair itself — no further input needed here.
        </p>

        <div style={styles.qrShell}>
          <div
            style={{
              ...styles.qrInner,
              boxShadow: phase === 'pairing'
                ? `0 0 0 2px ${COL.orange}, 0 24px 48px rgba(255,107,43,0.35)`
                : `0 0 0 1px ${COL.rule}, 0 24px 60px rgba(0,0,0,0.6)`,
              transition: 'box-shadow 600ms ease',
            }}
          >
            {pairingCode ? (
              <QRCodeSVG
                value={pairingCode}
                size={220}
                bgColor="transparent"
                fgColor={COL.bone}
                level="M"
              />
            ) : (
              <div style={{ width: 220, height: 220 }} />
            )}
          </div>
        </div>

        <div style={styles.codeRow}>
          {splitCode(formattedCode).map((char, i) =>
            char === '-' ? (
              <span key={i} style={styles.codeDash}>
                —
              </span>
            ) : (
              <span key={i} style={styles.codeCell}>
                {char}
              </span>
            ),
          )}
        </div>

        {phase === 'error' && error && (
          <div style={styles.errorBox}>{error}</div>
        )}

        <div style={styles.helpRow}>
          <Help step="1" text="Dashboard → Boards → Add Board" />
          <Help step="2" text="Enter the code above" />
          <Help step="3" text="Click Connect — this screen will follow" />
        </div>
      </div>

      {/* Bottom rail */}
      <div style={styles.footer}>
        <span style={styles.footerLabel}>SENSAR PLAYER</span>
        <span style={styles.footerDot}>·</span>
        <span style={styles.footerLabel}>v{getVersion()}</span>
      </div>
    </div>
  );
}

// ── Subcomponents ──────────────────────────────────────────────────

function BackgroundChrome({ tick }: { tick: number }) {
  const drift = (tick % 360) / 6;
  return (
    <>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(1200px 700px at 50% 30%, rgba(255,107,43,0.06), transparent 65%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(244,241,236,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(244,241,236,0.03) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage:
            'radial-gradient(900px 600px at 50% 40%, #000 30%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(900px 600px at 50% 40%, #000 30%, transparent 80%)',
          transform: `translate(${drift}px, ${drift / 1.5}px)`,
          transition: 'transform 800ms linear',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}

function Logo({ height = 24 }: { height?: number }) {
  // Inline so colours can be tuned for the dark background (the
  // public/SensarLogo.svg uses #111827 which disappears here).
  return (
    <svg
      width={height * 1.43}
      height={height}
      viewBox="0 0 220 154"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Sensar"
    >
      <path
        d="M219.634 82.8357C219.634 56.6668 198.42 35.4631 172.261 35.4631C159.763 35.4631 148.415 40.3112 139.944 48.2066C136.577 21.6434 114.532 0.279923 87.1909 0.0028903C57.037 -0.306108 32.5302 24.2006 32.8392 54.3546C33.1375 82.8144 56.2698 105.531 84.3247 107.428L84.6976 130.219C84.6976 142.952 95.0224 153.277 107.755 153.277H174.957C187.69 153.277 198.015 142.952 198.015 130.219C198.015 127.8 197.642 125.467 196.949 123.282C210.556 114.961 219.634 99.9585 219.634 82.8464V82.8357Z"
        fill={COL.bone}
      />
      <path
        d="M84.687 112.18V130.208C84.687 142.941 95.0117 153.266 107.745 153.266L84.6976 153.17H43.6967C20.1276 153.17 0.0640911 134.502 0.00016043 110.933C-0.0637702 87.4601 18.9875 68.4194 42.4608 68.4726C66.0299 68.5366 84.6976 88.6001 84.6976 112.169V125.2L84.687 112.169V112.18Z"
        fill={COL.orange}
      />
      <path
        d="M101.693 114.066C102.023 115.302 102.609 116.421 103.792 117.241C105.784 118.626 108.373 118.243 110.504 117.412C122.619 112.691 135.245 106.831 144.792 97.8275C146.018 96.6767 147.2 95.4194 147.861 93.8744C149.225 90.6779 147.254 87.6518 144.92 85.5527C144.398 85.0839 143.844 84.6364 143.301 84.1995C134.148 76.8795 124.004 71.4347 113.477 66.3949C110.611 65.0203 107.02 64.0614 103.994 65.6277C100.904 67.2366 101 70.6036 100.755 73.6084C100.403 78.0196 100.201 82.4414 100.137 86.874C100.073 91.477 100.169 96.0907 100.414 100.694C100.648 105.03 100.584 109.857 101.693 114.066Z"
        fill={COL.bg}
      />
    </svg>
  );
}

function Dot({ color, pulse }: { color: string; pulse?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 7,
        height: 7,
        borderRadius: '50%',
        backgroundColor: color,
        marginRight: 8,
        boxShadow: pulse ? `0 0 0 0 ${color}80` : undefined,
        animation: pulse ? 'sensar-dot-pulse 1.6s ease-out infinite' : undefined,
      }}
    />
  );
}

function Help({ step, text }: { step: string; text: string }) {
  return (
    <div style={styles.helpItem}>
      <span style={styles.helpStep}>{step}</span>
      <span style={styles.helpText}>{text}</span>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

function phaseLabel(phase: 'init' | 'waiting' | 'pairing' | 'error'): string {
  switch (phase) {
    case 'init':
      return 'INITIALISING';
    case 'waiting':
      return 'WAITING FOR DASHBOARD';
    case 'pairing':
      return 'PAIRING';
    case 'error':
      return 'ERROR';
  }
}

function splitCode(code: string): string[] {
  return Array.from(code);
}

function humanizeError(msg: string): string {
  if (msg.includes('400') || msg.toLowerCase().includes('invalid')) {
    return 'Pairing code expired or already used. Restart the device to generate a new one.';
  }
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
    return 'Network is unreachable. The display will keep trying.';
  }
  return 'Cannot reach the Sensar server. Check the network and try again.';
}

function getVersion(): string {
  // Replaced at bundle build time via Vite `define` (vite.config.bundle.ts).
  const v = (
    globalThis as { __SENSAR_BUNDLE_VERSION__?: string }
  ).__SENSAR_BUNDLE_VERSION__;
  return v ?? 'dev';
}

// ── Styles ─────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    backgroundColor: COL.bg,
    color: COL.bone,
    fontFamily: FONT_DISPLAY,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  topBar: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '28px 40px',
    zIndex: 2,
  },
  status: {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: FONT_MONO,
    fontSize: 11,
    letterSpacing: 2,
    color: COL.graySoft,
  },
  center: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 40px',
    zIndex: 2,
  },
  eyebrow: {
    fontFamily: FONT_MONO,
    fontSize: 11,
    color: COL.orange,
    letterSpacing: 4,
    marginBottom: 12,
  },
  title: {
    margin: 0,
    fontSize: 56,
    fontWeight: 600,
    letterSpacing: -1,
    color: COL.bone,
    textAlign: 'center',
  },
  subtitle: {
    marginTop: 14,
    maxWidth: 540,
    textAlign: 'center',
    color: COL.graySoft,
    fontSize: 15,
    lineHeight: 1.65,
  },
  qrShell: {
    marginTop: 40,
    padding: 24,
    borderRadius: 28,
    backgroundColor: COL.panel,
    border: `1px solid ${COL.rule}`,
  },
  qrInner: {
    padding: 20,
    borderRadius: 18,
    backgroundColor: '#0F0F12',
    display: 'flex',
  },
  codeRow: {
    display: 'flex',
    gap: 8,
    marginTop: 28,
    fontFamily: FONT_MONO,
    fontSize: 30,
    fontWeight: 600,
  },
  codeCell: {
    width: 36,
    height: 48,
    borderRadius: 8,
    backgroundColor: COL.panel,
    border: `1px solid ${COL.rule}`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COL.bone,
  },
  codeDash: {
    width: 16,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COL.grayFaint,
    fontSize: 26,
  },
  errorBox: {
    marginTop: 22,
    padding: '12px 18px',
    borderRadius: 12,
    color: '#FCA5A5',
    backgroundColor: 'rgba(220,38,38,0.10)',
    border: '1px solid rgba(220,38,38,0.35)',
    maxWidth: 540,
    textAlign: 'center',
    fontSize: 13,
    lineHeight: 1.55,
  },
  helpRow: {
    display: 'flex',
    gap: 32,
    marginTop: 36,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  helpItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
  },
  helpStep: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: COL.panel,
    border: `1px solid ${COL.rule}`,
    color: COL.bone,
    fontFamily: FONT_MONO,
    fontSize: 12,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  helpText: {
    color: COL.graySoft,
    fontSize: 13,
    letterSpacing: 0.2,
  },
  footer: {
    padding: '24px 40px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontFamily: FONT_MONO,
    fontSize: 11,
    letterSpacing: 2,
    color: COL.grayFaint,
    zIndex: 2,
  },
  footerLabel: {
    color: COL.grayFaint,
  },
  footerDot: {
    color: COL.grayFaint,
  },
};

// ── Keyframe injected once ──────────────────────────────────────────

if (typeof document !== 'undefined' && !document.getElementById('sensar-provision-kf')) {
  const style = document.createElement('style');
  style.id = 'sensar-provision-kf';
  style.textContent = `
    @keyframes sensar-dot-pulse {
      0%   { box-shadow: 0 0 0 0   rgba(255,107,43,0.55); }
      70%  { box-shadow: 0 0 0 10px rgba(255,107,43,0);  }
      100% { box-shadow: 0 0 0 0    rgba(255,107,43,0);  }
    }
    @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  `;
  document.head.appendChild(style);
}
